import React, { useEffect, useRef, useState } from 'react';
import MuxPlayer from '@mux/mux-player-react';
import { PLAYER_THEMES } from '@/player';
import useMuxChapters from '@/hook/useMuxChapters';
import { extractPlaybackAndToken } from '@/utils/extractPlaybackIdAndTokenFromMuxURL';
import { useDispatch } from 'react-redux';
import { setVideoPlaying } from '@/redux/sessionSlice';
import { getDataFromBrowser, getStringFromBrowser } from '@/utils/browserStorage';

const MuxVideoPlayer = ({
	CustomWrapper,
	videoLink,
	videoTitle,
	poster,
	storyboardToken,
	userId,
	videoId,
	chapters = [],
	errorMessage,
	videoCategory,
	userEmail,
	videoType
}) => {
	const playerRef = useRef(null);
	const dispatch = useDispatch();
	const isEligibleRef = useRef(false);
	const lastCheckRef = useRef(0);
	const lastSentWatchTimeRef = useRef(0);
	const accumulatedWatchRef = useRef(0);
	const lastPlayerTimeRef = useRef(0);
	const [sessionId] = useState(`session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`);
	const WrapperComponent = CustomWrapper || React.Fragment;
	const { playbackId, token } = videoLink ? extractPlaybackAndToken(videoLink) : { playbackId: null, token: null };
	const muxChapters = useMuxChapters(chapters);
	const userDataFromStorage = getDataFromBrowser('userData') || {};
	const jwtToken = getStringFromBrowser('token')?.replace(/"/g, '');
	
	let userIdFromToken = null;
	if (jwtToken) {
		const payload = JSON.parse(atob(jwtToken.split('.')[1]));
		userIdFromToken = payload._id;
	}
	const actualUserId = userId || userDataFromStorage?.user?._id || userDataFromStorage?._id || userIdFromToken;

	// Send watch heartbeat function
	const sendWatchHeartbeat = async (currentWatchTime) => {
		if (!actualUserId || !videoId) return;

		const delta = currentWatchTime - lastSentWatchTimeRef.current;
		if (delta < 5) return; // ignore tiny noise

		lastSentWatchTimeRef.current = currentWatchTime;

		await fetch(`${import.meta.env.VITE_APP_API_URL}/user-analytics/track/video-watch`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${getStringFromBrowser('token')?.replace(/"/g, '')}`
			},
			credentials: 'include',
			body: JSON.stringify({
				userId: actualUserId,
				videoId,
				sessionId,
				watchTimeSeconds: Math.floor(currentWatchTime)
			})
		});
	};

	// Track video completion based on type
	const trackVideoCompletion = async (videoType) => {
		if (!actualUserId || !videoType) return;

		const email = userEmail || 'unknown';

		let endpoint = '';
		switch (videoType?.toLowerCase()) {
			case 'class':
				endpoint = 'track/class-completed';
				break;
			case 'exercise':
				endpoint = 'track/exercise-completed';
				break;
			case 'lesson':
				endpoint = 'track/lesson-completed';
				break;
			default:
				return;
		}

		await fetch(`${import.meta.env.VITE_APP_API_URL}/user-analytics/${endpoint}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${getStringFromBrowser('token')?.replace(/"/g, '')}`
			},
			credentials: 'include',
			body: JSON.stringify({
				userId: actualUserId,
				email: email
			})
		});
	};

	useEffect(() => {
		const player = playerRef.current;
		if (!player || muxChapters.length === 0) return;

		if (player.readyState >= 1) {
			player.addChapters(muxChapters);
		}
	}, [muxChapters]);

	const addChaptersOnLoadedMetadata = () => {
		const player = playerRef.current;
		if (!player || muxChapters.length === 0) return;

		player.addChapters(muxChapters);
	};

	const handlePlay = () => {
		dispatch(setVideoPlaying(true));
	};

	const handlePause = () => {
		dispatch(setVideoPlaying(false));
	};

	const handleEnded = () => {
		dispatch(setVideoPlaying(false));
		const player = playerRef.current;
		if (!player) return;

		const duration = player.duration;
		const watched = accumulatedWatchRef.current;

		sendWatchHeartbeat(watched);

		// Require 80% real watch
		if (videoType && watched >= duration * 0.8) {
			trackVideoCompletion(videoType);
		}
	};

	const handleTimeUpdate = () => {
		const player = playerRef.current;
		if (!player) return;

		const now = Date.now();
		if (now - lastCheckRef.current < 2000) return;
		lastCheckRef.current = now;

		const currentTime = player.currentTime;

		let delta = currentTime - lastPlayerTimeRef.current;

		// ignore backward seek
		if (delta < 0) delta = 0;

		// ignore unrealistic forward seek (>3s jump)
		if (delta > 3) delta = 0;

		accumulatedWatchRef.current += delta;
		lastPlayerTimeRef.current = currentTime;

		if (!isEligibleRef.current && currentTime >= 30) {
			isEligibleRef.current = true;
		}

		if (!isEligibleRef.current) return;

		sendWatchHeartbeat(accumulatedWatchRef.current);
	};

	// Reset refs on new video
	useEffect(() => {
		lastSentWatchTimeRef.current = 0;
		isEligibleRef.current = false;
		lastCheckRef.current = 0;
		accumulatedWatchRef.current = 0;
		lastPlayerTimeRef.current = 0;
	}, [videoId]);

	// Handle tab close/refresh
	useEffect(() => {
		const flushWatchTime = () => {
			sendWatchHeartbeat(accumulatedWatchRef.current);
		};

		window.addEventListener('beforeunload', flushWatchTime);
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'hidden') flushWatchTime();
		});

		return () => {
			window.removeEventListener('beforeunload', flushWatchTime);
		};
	}, []);

	useEffect(() => {
		return () => {
			sendWatchHeartbeat(accumulatedWatchRef.current);
		};
	}, []);

	useEffect(() => {
		if (typeof window !== 'undefined' && !window.customElements.get(PLAYER_THEMES.SUTRO.id)) {
			import('https://cdn.jsdelivr.net/npm/media-chrome/dist/media-theme-element.js').then(({ MediaThemeElement }) => {
				const template = document.createElement('template');
				template.innerHTML = PLAYER_THEMES.SUTRO.template;

				class MediaThemeCustom extends MediaThemeElement {
					static template = template;
				}

				if (!window.customElements.get(PLAYER_THEMES.SUTRO.id)) {
					window.customElements.define(PLAYER_THEMES.SUTRO.id, MediaThemeCustom);
				}
			});
		}
	}, []);

	return (
		<WrapperComponent>
			{videoLink ? (
				<div onContextMenu={e => e.preventDefault()} className='player-wrapper'>
					<MuxPlayer
						ref={playerRef}
						className="mux-player"
						theme={PLAYER_THEMES.SUTRO.title}
						maxResolution="2160p"
						playbackId={playbackId}
						preferPlayback="mse"
						metadata={{
							custom_1: String(videoCategory || 'Uncategorized').trim() || 'Uncategorized',
							custom_2: String(videoId || '').trim() || null
						}}
						poster={poster && poster}
						thumbnailTime={!poster && 0.5}
						metadataViewerUserId={userEmail}
						metadataVideoId={String(videoId)}
						metadataVideoTitle={videoTitle}
						primaryColor="white"
						secondaryColor="#008bc3"
						accentColor="#008bc3"
						hotkeys="noarrowleft noarrowright"
						tokens={{ playback: token, storyboard: storyboardToken }}
						onLoadedMetadata={addChaptersOnLoadedMetadata}
						onPlay={handlePlay}
						onPause={handlePause}
						onEnded={handleEnded}
						onTimeUpdate={handleTimeUpdate}
						defaultHiddenCaptions
					/>
				</div>
			) : (
				<div>{errorMessage || 'loading....'}</div>
			)}
		</WrapperComponent>
	);
};

export default MuxVideoPlayer;
